import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Router, Route, Redirect, Switch } from 'react-router-dom';
import { Image } from 'semantic-ui-react';
import config from '../config';
import { history } from '../helpers';
import { Dialog } from './dialog/Dialog';
import {
  commonActions,
  uiActions,
  userActions,
  projectActions,
  teamMemberActions,
  projectActivityActions,
  projectConnectActivityActions,
  paymentActions,
} from './../actions';
import Splash from './Splash';
import Login from './Login';
import Welcome from './Welcome';
import Main from './Main';
import Strings from '../common/Strings';
import './App.css';
import './Common.css';

const firebase = require('firebase/app');
require('firebase/auth');
require('firebase/firestore');

// Snapshot listener remove methods
let userSnapshot;

class App extends Component {
  static stopSync() {
    if (userSnapshot !== undefined) {
      userSnapshot();
    }

    projectActions.stopSync();
    teamMemberActions.stopSync();
    projectActivityActions.stopSync();
    projectConnectActivityActions.stopSync();
  }

  constructor(props) {
    super(props);
    // Initialize Firebase
    const firebaseConfig = {
      apiKey: config.FIREBASE_API_KEY,
      authDomain: config.FIREBASE_AUTH_DOMAIN,
      databaseURL: config.FIREBASE_DATABASE_URL,
      projectId: config.FIREBASE_PROJECT_ID,
      storageBucket: config.FIREBASE_STORAGE_BUCKET,
      messagingSenderId: config.FIREBASE_MESSAGING_SENDER_ID,
    };

    firebase.initializeApp(firebaseConfig);

    // Listen to history stack to clear alerts when page changes
    const { dispatch } = this.props;
    history.listen(() => {
      dispatch(uiActions.closeDialog());
    });

    firebase.auth().onAuthStateChanged(user => {
      if (user) {
        // User is signed in.
        dispatch(userActions.fetchUser());
        dispatch(this.setupUserSync());
        dispatch(projectActions.fetchProjects());
        dispatch(teamMemberActions.fetchTeamMembers());
        dispatch(projectActivityActions.fetchRecentWorkLogs());
        dispatch(projectConnectActivityActions.fetchProjectConnectActivity());
        dispatch(paymentActions.getPaymentCards());
        dispatch(paymentActions.getPaymentHistory());
        dispatch(paymentActions.getUpcomingInvoice());
        dispatch(paymentActions.checkSubscription());
      } else {
        // User is signed out.
        App.stopSync();
        dispatch(commonActions.clearStore());
        dispatch(uiActions.initialized(true));
        history.replace('login');
      }
    });
  }

  setupUserSync = () => dispatch => {
    const { currentUser } = firebase.auth();
    if (currentUser) {
      userSnapshot = firebase
        .firestore()
        .collection('users')
        .doc(currentUser.uid)
        .onSnapshot(snapshot => {
          // When hasPendingWrites is false, it means trigger came from server
          if (!snapshot.metadata.hasPendingWrites) {
            const user = snapshot.data();

            // Check if user data changes
            if (user) {
              dispatch(userActions.settingsUpdated(user));
            }
          }
        });
    }
  };

  renderDialog() {
    if (this.props.dialog) {
      return (
        <Dialog
          image={this.props.dialog.image}
          title={this.props.dialog.title}
          body={this.props.dialog.body}
          buttonStyle={this.props.dialog.buttonStyle}
          buttonText={this.props.dialog.buttonText}
          buttonOnClick={() => {
            if (this.props.dialog.buttonOnClick) {
              this.props.dialog.buttonOnClick();
            }
            this.props.dispatch(uiActions.closeDialog());
          }}
          onClose={() => this.props.dispatch(uiActions.closeDialog())}
        />
      );
    }
    return null;
  }

  renderUpdateAvailable() {
    if (this.props.isUpdateAvailable) {
      return (
        <div className="app-update-available-container">
          <Image
            className="app-update-available-logo"
            src={'/emoji/update-available.png'}
          />
          <p className="app-update-available-title">
            {Strings.COMMON.updateAvailable}
          </p>
          <p
            onClick={() => window.location.reload()}
            className="app-update-available-refresh"
          >
            {Strings.COMMON.refreshNow}
          </p>
        </div>
      );
    }
    return null;
  }

  render() {
    if (!this.props.isInitialized) {
      return (
        <div className="common-app-root">
          <Router history={history}>
            <Switch>
              <Route path="/" component={Splash} />
              <Route render={() => <Redirect to="/" />} />
            </Switch>
          </Router>
        </div>
      );
    }

    const { email, companyName } = this.props.user;
    if (email.length > 0) {
      if (companyName && companyName.length > 0) {
        return (
          <div className="common-app-root">
            {this.renderDialog()}
            <Router history={history}>
              <Switch>
                <Route path="/" component={Main} />
                <Route render={() => <Redirect to="/" />} />
              </Switch>
            </Router>
            {this.renderUpdateAvailable()}
          </div>
        );
      }
      return (
        <div className="common-app-root">
          {this.renderDialog()}
          <Router history={history}>
            <Switch>
              <Route path="/welcome" component={Welcome} />
              <Route render={() => <Redirect to="/welcome" />} />
            </Switch>
          </Router>
          {this.renderUpdateAvailable()}
        </div>
      );
    }
    return (
      <div className="common-app-root">
        {this.renderDialog()}
        <Router history={history}>
          <Switch>
            <Route path="/login" component={Login} />
            <Route path="/signup" component={Login} />
            <Route render={() => <Redirect to="/login" />} />
          </Switch>
        </Router>
        {this.renderUpdateAvailable()}
      </div>
    );
  }
}

App.propTypes = {
  dispatch: PropTypes.func.isRequired,
  isInitialized: PropTypes.bool.isRequired,
  user: PropTypes.shape({
    email: PropTypes.string.isRequired,
    companyName: PropTypes.string.isRequired,
    uid: PropTypes.string.isRequired,
  }).isRequired,
  dialog: PropTypes.shape({
    image: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
    buttonStyle: PropTypes.string.isRequired,
    buttonText: PropTypes.string.isRequired,
    buttonOnClick: PropTypes.string.isRequired,
  }).isRequired,
  isUpdateAvailable: PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
  isInitialized: state.uiState.isInitialized,
  user: state.user,
  dialog: state.uiState.dialog,
  isUpdateAvailable: state.uiState.isUpdateAvailable,
});

export default connect(mapStateToProps)(App);



// WEBPACK FOOTER //
// ./src/components/App.js